import { useEffect, useMemo, useState } from 'react';
import { pagesApi, promoPreviewUrl, type PagesSite, type PromoListItem } from '../../api/pages';
import { useFeather } from '../../shell/useFeather';
import { Button } from '../../components/arc/button/button';
import { ArcPanel, ArcTableFrame } from '../../components/arc/primitives/arc-primitives';
import { AffilmasterDetailSectionHeader } from '../AffilmasterDetailFields';
import './stranky.css';

const STATUS_LABEL: Record<string, string> = { draft: 'Koncept', published: 'Publikováno', expired: 'Expirováno' };

function fmtDate(v: string | null): string {
  return v ? new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' }).format(new Date(v)) : '';
}

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function EditIcon(): JSX.Element {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>;
}

function PreviewIcon(): JSX.Element {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>;
}

export function KlientPromoNews({ partnerId, onOpen }: { partnerId: number; onOpen?: (id: number) => void }): JSX.Element {
  const [items, setItems] = useState<PromoListItem[] | null>(null);
  const [sites, setSites] = useState<PagesSite[]>([]);
  const [error, setError] = useState<string | null>(null);
  useFeather([items, sites]);

  useEffect(() => {
    void pagesApi.sites().then(setSites).catch(() => setSites([]));
  }, []);

  useEffect(() => {
    let alive = true;
    pagesApi.promoList({ partnerId, includeExpired: 1, limit: 200 })
      .then((r) => { if (alive) setItems(r.items); })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [partnerId]);

  const months = useMemo(() => {
    const out: Array<{ key: string; label: string; count: number }> = [];
    const now = new Date();
    for (let i = 0; i < 12; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = monthKey(d);
      const count = (items ?? []).filter((it) => it.status !== 'draft' && it.publishedAt && monthKey(new Date(it.publishedAt)) === key).length;
      out.push({ key, label: new Intl.DateTimeFormat('cs-CZ', { month: 'short', year: '2-digit' }).format(d), count });
    }
    return out;
  }, [items]);

  if (error) return <div className="text-danger small">{error}</div>;
  if (!items) return <div className="text-muted small">Načítám…</div>;
  const visibleItems = items.filter((i) => i.status !== 'expired');
  const timelineMonths = months;
  const deliveredCount = months.reduce((sum, month) => sum + month.count, 0);
  const activeMonthsCount = months.filter((month) => month.count > 0).length;
  const currentMonthCount = months[0]?.count ?? 0;
  const previewOf = (it: PromoListItem): string | null => {
    if (it.status === 'draft') return null;
    const site = sites.find((s) => s.id === it.siteId);
    return promoPreviewUrl(site?.locale === 'sk' ? 'sk' : 'cs', it.partnerSlug, it.slug);
  };
  return (
    <div className="st-promo-tab">
      <ArcPanel as="section" className="st-promo-delivery" aria-labelledby="st-promo-delivery-title">
        <header className="st-promo-delivery-head">
          <div>
            <span>DODÁVÁNÍ PROMO NEWS</span>
            <h3 id="st-promo-delivery-title">Posledních 12 měsíců</h3>
          </div>
          <dl className="st-promo-delivery-summary">
            <div><dt>Tento měsíc</dt><dd>{currentMonthCount}</dd></div>
            <div><dt>Aktivní měsíce</dt><dd>{activeMonthsCount}<small>/ 12</small></dd></div>
            <div><dt>Za 12 měsíců</dt><dd>{deliveredCount}</dd></div>
          </dl>
        </header>
        <div className="st-promo-month-scroll">
          <div className="st-promo-month-timeline" role="list" aria-label="Počet dodaných Promo News po měsících">
            {timelineMonths.map((month) => (
              <div key={month.key} className={`${month.count ? 'has-data' : 'is-empty'}${month.key === months[0]?.key ? ' is-current' : ''}`} role="listitem" title={`${month.label}: ${month.count} Promo News`}>
                <span>{month.label}</span>
                <strong>{month.count}</strong>
                <i aria-hidden="true" />
              </div>
            ))}
          </div>
        </div>
      </ArcPanel>
      <AffilmasterDetailSectionHeader title="Poslední Promo News" description={`${visibleItems.length} aktuálních`} />
      {visibleItems.length === 0 ? <div className="st-client-promo-empty">Klient nemá žádné aktuální Promo News.</div> : (
        <ArcTableFrame className="st-client-promo-table-shell" label="Promo News klienta"><table className="table table-sm st-table st-client-promo-table">
          <thead><tr><th>Publikováno</th><th>Název</th><th>Přílohy</th><th>Stav</th><th className="st-action-column"><span className="visually-hidden">Akce</span></th></tr></thead>
          <tbody>
            {visibleItems.map((it) => {
              const preview = previewOf(it);
              const open = () => { if (preview) window.open(preview, '_blank', 'noopener'); else onOpen?.(it.id); };
              return (
                <tr key={it.id} onClick={open}>
                  <td className="text-nowrap">{fmtDate(it.publishedAt)}</td>
                  <td>{it.title}</td>
                  <td>{it.filesCount || '–'}</td>
                  <td><span className={`st-badge ${it.status === 'published' ? 'pub' : ''}`}>{STATUS_LABEL[it.status]}</span></td>
                  <td className="st-action-column">
                    <span className="st-akce">
                      {onOpen ? <Button type="button" variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onOpen(it.id); }} title={`Upravit ${it.title}`} aria-label={`Upravit ${it.title}`}><EditIcon /></Button> : null}
                      <Button type="button" variant="ghost" size="sm" disabled={!preview} onClick={(e) => { e.stopPropagation(); if (preview) window.open(preview, '_blank', 'noopener'); }} title={preview ? `Náhled ${it.title}` : 'Náhled je dostupný po publikaci'} aria-label={preview ? `Náhled ${it.title}` : `Náhled ${it.title} je dostupný po publikaci`}><PreviewIcon /></Button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></ArcTableFrame>
      )}
    </div>
  );
}
